/**
 * Service for managing athlete sessions
 */

import { buildApiUrl } from '../utils/apiConfig';
import { CreateSessionRequest, UpdateSessionRequest, SessionResponse } from '../types/sessions';
import { PageResponse } from '../types/pagination';

/**
 * Fetches a page of sessions for a specific athlete
 * @param athleteId - The athlete's ID
 * @param page - Zero-based page number
 * @param size - Page size
 * @returns Promise<PageResponse<SessionResponse>> - Paginated sessions
 */
export const fetchAthleteSessionsApi = async (
  athleteId: number,
  page = 0,
  size = 10
): Promise<PageResponse<SessionResponse>> => {
  const url = buildApiUrl(`api/athletes/${athleteId}/sessions?page=${page}&size=${size}`);

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });

  if (response.status === 401) {
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (response.status === 404) {
    return { content: [], totalElements: 0, totalPages: 0, size, number: 0,
             pageable: { pageNumber: 0, pageSize: size, offset: 0,
                         sort: { sorted: false, unsorted: true, empty: true },
                         paged: true, unpaged: false },
             last: true, first: true, numberOfElements: 0, empty: true };
  }

  if (!response.ok) {
    throw new Error('Failed to fetch sessions');
  }

  return await response.json();
};

/**
 * Creates a new session for an athlete
 * @param athleteId - The athlete's ID
 * @param sessionData - The session data to create
 * @returns Promise<SessionResponse> - The created session
 */
export const createSessionApi = async (
  athleteId: number,
  sessionData: CreateSessionRequest
): Promise<SessionResponse> => {
  const url = buildApiUrl(`api/athletes/${athleteId}/sessions`);

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify(sessionData)
  });

  if (response.status === 401) {
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    // Handle validation errors from backend
    if (errorData.validationErrors) {
      const validationMessages = Object.entries(errorData.validationErrors)
        .map(([field, message]) => `${field}: ${message}`)
        .join(', ');
      throw new Error(validationMessages);
    }

    throw new Error(errorData.detail || errorData.message || 'Failed to create session');
  }

  return await response.json();
};

/**
 * Updates an existing session
 * @param athleteId - The athlete's ID
 * @param sessionId - The session's ID
 * @param sessionData - The session data to update
 * @returns Promise<SessionResponse> - The updated session
 */
export const updateSessionApi = async (
  athleteId: number,
  sessionId: number,
  sessionData: UpdateSessionRequest
): Promise<SessionResponse> => {
  const url = buildApiUrl(`api/athletes/${athleteId}/sessions/${sessionId}`);

  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify(sessionData)
  });

  if (response.status === 401) {
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    // Handle validation errors from backend
    if (errorData.validationErrors) {
      const validationMessages = Object.entries(errorData.validationErrors)
        .map(([field, message]) => `${field}: ${message}`)
        .join(', ');
      throw new Error(validationMessages);
    }

    throw new Error(errorData.detail || errorData.message || 'Failed to update session');
  }

  return await response.json();
};

/**
 * Deletes a session
 * @param athleteId - The athlete's ID
 * @param sessionId - The session's ID
 * @returns Promise<void>
 */
export const deleteSessionApi = async (
  athleteId: number,
  sessionId: number
): Promise<void> => {
  const url = buildApiUrl(`api/athletes/${athleteId}/sessions/${sessionId}`);

  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });

  if (response.status === 401) {
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    // Handle validation errors from backend
    if (errorData.validationErrors) {
      const validationMessages = Object.entries(errorData.validationErrors)
        .map(([field, message]) => `${field}: ${message}`)
        .join(', ');
      throw new Error(validationMessages);
    }

    throw new Error(errorData.detail || errorData.message || 'Failed to delete session');
  }
};

